import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { smlNavigationMessages } from '../../navigation/smlNavigationMessages';
import { PromoteContainer } from '../PromoteContainer';
import { useProfileData } from './useProfileData';
import { useProfileRoute } from './useProfileRoute';
import { profileMessages as m } from './profileMessages';
import { PROFILE_TOOL_TITLES } from './tools/profileToolTitles';
import { ProfilePage } from './page/ProfilePage';
import { ProfileHeadActions } from './page/ProfileHeadActions';
import { ProfileToolScreen } from './tools/ProfileToolScreen';
import { ProfileBioTool } from './bio/ProfileBioTool';
import { SpotifyBioSheet } from './spotify/SpotifyBioSheet';
import { SendAllSheet } from './sendAll/SendAllSheet';

interface ProfileScreenProps {
  accountId: number;
}

/**
 * Studio → Profile (spec 2026-10-17 artist Profile, design artist-profile a1–a3, b, c, d). The URL
 * names the view (`useProfileRoute`): the page, one tool (`?tool=`), and a sheet over either:
 * "Send to all" (`&sheet=sendAll`, design profile-card c), the same sheet with only Spotify ticked
 * for the Spotify upload (`&sheet=spotify`), and the bio upload to Spotify (`&sheet=spotifyBio`).
 * LoadingView while the profile loads, ErrorView with retry when it does not.
 */
const ProfileScreenComponent: React.FC<ProfileScreenProps> = ({ accountId }) => {
  const intl = useIntl();
  const route = useProfileRoute();
  const { data, error, isLoading, refetch } = useProfileData(accountId);
  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  const studio = intl.formatMessage(smlNavigationMessages.promoteStudio);
  const profileName = intl.formatMessage(m.crumb);
  const toolTitle = route.tool === null ? null : intl.formatMessage(PROFILE_TOOL_TITLES[route.tool].name);
  const crumbs = useMemo(
    () => (toolTitle === null ? [studio, profileName] : [studio, profileName, toolTitle]),
    [profileName, studio, toolTitle]
  );
  const title = toolTitle ?? intl.formatMessage(m.title);
  const subtitle =
    route.tool === null ? intl.formatMessage(m.subtitle) : intl.formatMessage(PROFILE_TOOL_TITLES[route.tool].sub);

  if (isLoading) {
    return <LoadingView />;
  }

  const actions =
    data !== undefined && route.tool === null ? (
      <ProfileHeadActions accountId={accountId} profile={data} onSendAll={route.openSendAllSheet} />
    ) : undefined;

  return (
    <PromoteContainer title={title} subtitle={subtitle} crumbs={crumbs} actions={actions}>
      {data === undefined && <ErrorView error={error} retry={retry} />}
      {data !== undefined && route.tool === null && <ProfilePage accountId={accountId} profile={data} route={route} />}
      {data !== undefined && route.tool === 'bio' && (
        <ProfileBioTool accountId={accountId} profile={data} route={route} />
      )}
      {data !== undefined && route.tool !== null && route.tool !== 'bio' && (
        <ProfileToolScreen key={route.tool} accountId={accountId} profile={data} tool={route.tool} route={route} />
      )}
      {data !== undefined && (route.sheet === 'sendAll' || route.sheet === 'spotify') && (
        <SendAllSheet
          accountId={accountId}
          profile={data}
          onClose={route.close}
          only={route.sheet === 'spotify' ? 'spotify' : null}
        />
      )}
      {data !== undefined && (
        <SpotifyBioSheet
          accountId={accountId}
          profile={data}
          isOpen={route.sheet === 'spotifyBio'}
          onClose={route.close}
        />
      )}
    </PromoteContainer>
  );
};

export const ProfileScreen = TypedMemo(ProfileScreenComponent);
